// src/components/vehicles/WorkshopManagerPanel.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { api, baseURL, formatDate } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const COLUMNS = [
  { status: "OPEN", label: "Zgłoszone", icon: "pi pi-exclamation-triangle", colorVar: "--fleet-danger", tagCls: "fleet-tag--danger" },
  { status: "IN_PROGRESS", label: "W trakcie", icon: "pi pi-spin pi-cog", colorVar: "--fleet-warning", tagCls: "fleet-tag--warning" },
  { status: "RESOLVED", label: "Gotowe", icon: "pi pi-check-circle", colorVar: "--fleet-success", tagCls: "fleet-tag--success" },
];

const WorkshopManagerPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolveDialogVisible, setResolveDialogVisible] = useState(false);
  const [resolvingReport, setResolvingReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [repairCost, setRepairCost] = useState(null);
  const [meterType, setMeterType] = useState("mileage");
  const [mileage, setMileage] = useState(null);
  const [motorHours, setMotorHours] = useState(null);
  const [photosDialogVisible, setPhotosDialogVisible] = useState(false);
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [reopenDialogVisible, setReopenDialogVisible] = useState(false);
  const [reopeningReport, setReopeningReport] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [activeId, setActiveId] = useState(null);
  const { user } = useContext(AuthContext);
  const toast = useRef(null);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await api.get("/fault-reports", { params: { limit: 500, maxResolvedDays: 7 } });
      setReports(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // --- Actions ---
  const openResolve = (report) => {
    setResolvingReport(report);
    setResolutionNotes("");
    setRepairCost(null);
    setMeterType("mileage");
    setMileage(null);
    setMotorHours(null);
    setResolveDialogVisible(true);
  };

  const resolveReport = async () => {
    if (!resolvingReport) return;
    if (!repairCost && repairCost !== 0) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Podaj kwotę naprawy" }); return;
    }
    const mVal = meterType === "mileage" ? mileage : null;
    const mhVal = meterType === "motorHours" ? motorHours : null;
    if (!mVal && !mhVal) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Podaj przebieg lub motogodziny" }); return;
    }
    try {
      await api.put(`/fault-reports/${resolvingReport.id}/resolve`, {
        resolutionNotes, repairCost, mileage: mVal, motorHours: mhVal
      });
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Usterka oznaczona jako naprawiona" });
      setResolveDialogVisible(false);
      loadReports();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zaktualizować" });
    }
  };

  const markInProgress = async (report) => {
    try {
      await api.put(`/fault-reports/${report.id}/in-progress`);
      toast.current.show({ severity: "info", summary: "Info", detail: "Status: W trakcie naprawy" });
      loadReports();
    } catch (err) { console.error(err); }
  };

  const openReopen = (report) => {
    setReopeningReport(report);
    setReopenReason("");
    setReopenDialogVisible(true);
  };

  const reopenReport = async () => {
    if (!reopeningReport) return;
    if (!reopenReason.trim()) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Podaj powód cofnięcia statusu" });
      return;
    }
    try {
      await api.put(`/fault-reports/${reopeningReport.id}/reopen`, { reopenReason });
      toast.current.show({ severity: "info", summary: "Cofnięto", detail: "Zgłoszenie wróciło do statusu: W trakcie" });
      setReopenDialogVisible(false);
      loadReports();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Nie udało się cofnąć";
      toast.current.show({ severity: "error", summary: "Błąd", detail: msg });
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm("Usunąć to zgłoszenie?")) return;
    try {
      await api.delete(`/fault-reports/${reportId}`);
      toast.current.show({ severity: "success", summary: "Usunięto" });
      loadReports();
    } catch (err) { console.error(err); }
  };

  const showPhotos = async (report) => {
    setPhotosLoading(true);
    setPhotosDialogVisible(true);
    setCurrentPhotos([]);
    try {
      const res = await api.get(`/fault-reports/${report.id}/photos`);
      setCurrentPhotos(res.data);
    } catch (err) { console.error(err); }
    setPhotosLoading(false);
  };

  // --- dnd-kit handlers ---
  const findReportById = (id) => reports.find(r => r.id === Number(id));

  const findColumn = (id) => {
    if (["OPEN", "IN_PROGRESS", "RESOLVED"].includes(id)) return id;
    const report = findReportById(id);
    return report ? report.status : null;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const report = findReportById(active.id);
    if (!report) return;

    const targetStatus = findColumn(over.id);
    if (!targetStatus || report.status === targetStatus) return;

    // OPEN -> IN_PROGRESS
    if (report.status === "OPEN" && targetStatus === "IN_PROGRESS") {
      await markInProgress(report);
    }
    // OPEN/IN_PROGRESS -> RESOLVED
    else if ((report.status === "OPEN" || report.status === "IN_PROGRESS") && targetStatus === "RESOLVED") {
      openResolve(report);
    }
    // RESOLVED -> IN_PROGRESS (reopen)
    else if (report.status === "RESOLVED" && targetStatus === "IN_PROGRESS") {
      openReopen(report);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeReport = activeId ? findReportById(activeId) : null;

  // --- Grouped data ---
  const grouped = {
    OPEN: reports.filter(r => r.status === "OPEN"),
    IN_PROGRESS: reports.filter(r => r.status === "IN_PROGRESS"),
    RESOLVED: reports.filter(r => r.status === "RESOLVED"),
  };

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">🔧</span>Warsztat</h1>
          <p className="fleet-page-subtitle">Zarządzanie zgłoszeniami usterek ({reports.length} łącznie)</p>
        </div>
        <Button label="Odśwież" icon="pi pi-refresh" className="p-button-outlined" size="small" onClick={loadReports} loading={loading} />
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="kanban-board">
          {COLUMNS.map(col => (
            <KanbanColumn key={col.status} col={col} reports={grouped[col.status]} loading={loading}>
              {loading && grouped[col.status].length === 0 && (
                <div className="kanban-empty"><i className="pi pi-spin pi-spinner" /></div>
              )}
              {!loading && grouped[col.status].length === 0 && (
                <div className="kanban-empty">
                  <i className="pi pi-inbox" />
                  <span>Brak zgłoszeń</span>
                </div>
              )}
              {grouped[col.status].map(report => (
                <KanbanCard
                  key={report.id}
                  report={report}
                  col={col}
                  navigate={navigate}
                  showPhotos={showPhotos}
                  markInProgress={markInProgress}
                  openResolve={openResolve}
                  openReopen={openReopen}
                  deleteReport={deleteReport}
                  isAdmin={user?.role === "Administrator"}
                  formatDate={formatDate}
                />
              ))}
            </KanbanColumn>
          ))}
        </div>

        <DragOverlay dropAnimation={{ duration: 250, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
          {activeReport ? (
            <KanbanCardOverlay report={activeReport} formatDate={formatDate} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Resolve Dialog */}
      <Dialog
        header="Rozwiąż zgłoszenie"
        visible={resolveDialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setResolveDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setResolveDialogVisible(false)} />
            <Button label="Oznacz jako naprawione" icon="pi pi-check" className="fleet-btn-primary" onClick={resolveReport} />
          </div>
        }
      >
        {resolvingReport && (
          <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div style={{ background: "var(--fleet-info-bg, #eff6ff)", padding: "0.75rem 1rem", borderRadius: "var(--fleet-radius-sm)", fontSize: "0.85rem" }}>
              <strong>{resolvingReport.is_external ? `${resolvingReport.ext_vehicle_brand} ${resolvingReport.ext_vehicle_model || ''}` : `${resolvingReport.brand} ${resolvingReport.model}`}</strong> ({resolvingReport.is_external ? `${resolvingReport.ext_vehicle_registration} — zewnętrzny` : resolvingReport.registration_number})<br />
              <strong>Kategoria:</strong> {resolvingReport.category}<br />
              <strong>Problem:</strong> {resolvingReport.description}
            </div>
            <div className="fleet-form-field">
              <label>Kwota naprawy (PLN) *</label>
              <InputNumber value={repairCost} onValueChange={(e) => setRepairCost(e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={2} placeholder="0.00" />
            </div>
            <div className="fleet-form-field">
              <label style={{ marginBottom: "0.35rem" }}>Licznik *</label>
              <SelectButton
                value={meterType}
                onChange={(e) => { if (e.value) setMeterType(e.value); }}
                options={[{ label: "Przebieg (km)", value: "mileage" }, { label: "Motogodziny", value: "motorHours" }]}
                style={{ marginBottom: "0.5rem" }}
              />
              {meterType === "mileage" ? (
                <InputNumber value={mileage} onValueChange={(e) => setMileage(e.value)} placeholder="Przebieg w km" suffix=" km" />
              ) : (
                <InputNumber value={motorHours} onValueChange={(e) => setMotorHours(e.value)} placeholder="Motogodziny" mode="decimal" minFractionDigits={1} maxFractionDigits={1} suffix=" MTG" />
              )}
            </div>
            <div className="fleet-form-field">
              <label>Notatki z naprawy (opcjonalne)</label>
              <InputTextarea value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} rows={3} placeholder="Opisz co zostało zrobione..." />
            </div>
          </div>
        )}
      </Dialog>

      {/* Photos Dialog */}
      <Dialog
        header="Zdjęcia zgłoszenia"
        visible={photosDialogVisible}
        style={{ width: "640px", maxWidth: "95vw" }}
        onHide={() => setPhotosDialogVisible(false)}
        className="fleet-dialog"
      >
        {photosLoading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
          </div>
        ) : currentPhotos.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--fleet-text-secondary, #999)" }}>
            <i className="pi pi-image" style={{ fontSize: "2.5rem", marginBottom: "0.5rem", display: "block" }} />
            Brak zdjęć dla tego zgłoszenia
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {currentPhotos.map((photo) => (
              <a
                key={photo.id}
                href={`${baseURL.replace(/\/$/, "")}${photo.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "block" }}
              >
                <img
                  src={`${baseURL.replace(/\/$/, "")}${photo.file_path}`}
                  alt={photo.original_name}
                  style={{
                    width: "180px", height: "140px", objectFit: "cover",
                    borderRadius: "var(--fleet-radius-sm, 6px)",
                    border: "1px solid var(--fleet-border, #e0e0e0)",
                    cursor: "pointer"
                  }}
                />
              </a>
            ))}
          </div>
        )}
      </Dialog>

      {/* Reopen Dialog */}
      <Dialog
        header="Cofnij do: W trakcie"
        visible={reopenDialogVisible}
        style={{ width: "480px", maxWidth: "95vw" }}
        onHide={() => setReopenDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setReopenDialogVisible(false)} />
            <Button label="Cofnij status" icon="pi pi-undo" className="fleet-btn-primary" style={{ background: "var(--fleet-warning, #f59e0b)", borderColor: "var(--fleet-warning, #f59e0b)" }} onClick={reopenReport} />
          </div>
        }
      >
        {reopeningReport && (
          <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div style={{ background: "var(--fleet-warning-light, #fffbeb)", padding: "0.75rem 1rem", borderRadius: "var(--fleet-radius-sm)", fontSize: "0.85rem", border: "1px solid var(--fleet-warning, #f59e0b)" }}>
              <i className="pi pi-exclamation-triangle" style={{ color: "var(--fleet-warning, #f59e0b)", marginRight: "0.4rem" }} />
              Cofasz zgłoszenie <strong>{reopeningReport.is_external ? `${reopeningReport.ext_vehicle_brand} ${reopeningReport.ext_vehicle_model || ''}` : `${reopeningReport.brand} ${reopeningReport.model}`}</strong> ({reopeningReport.is_external ? `${reopeningReport.ext_vehicle_registration} — zewnętrzny` : reopeningReport.registration_number}) z <strong>Gotowe</strong> na <strong>W trakcie</strong>.
              <br /><small>Powiązana naprawa zostanie usunięta z historii.</small>
            </div>
            <div className="fleet-form-field">
              <label>Powód cofnięcia *</label>
              <InputTextarea value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={3} placeholder="Opisz dlaczego naprawa wymaga ponownego otwarcia..." autoFocus />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

// --- Droppable Column ---
const KanbanColumn = ({ col, reports, loading, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: col.status });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column${isOver ? " kanban-column--over" : ""}`}
    >
      <div className="kanban-column-header" style={{ "--col-color": `var(${col.colorVar})` }}>
        <div className="kanban-column-title">
          <i className={col.icon} />
          <span>{col.label}</span>
        </div>
        <span className="kanban-column-count">{reports.length}</span>
      </div>
      <div className="kanban-column-body">
        {children}
      </div>
    </div>
  );
};

// --- Draggable Card ---
const KanbanCard = ({ report, col, navigate, showPhotos, markInProgress, openResolve, openReopen, deleteReport, isAdmin, formatDate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: report.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kanban-card kanban-card--${col.status.toLowerCase()}${isDragging ? " kanban-card--dragging" : ""}`}
    >
      <div className="kanban-card-grip" title="Przeciągnij" {...attributes} {...listeners}>
        <i className="pi pi-ellipsis-v" /><i className="pi pi-ellipsis-v" />
      </div>
      <div className="kanban-card-content">
        <div className="kanban-card-top">
          <span className="kanban-card-vehicle" onClick={() => report.vehicle_id ? navigate(`/pojazdy/${report.vehicle_id}`) : null}>
            <i className="pi pi-car" /> {report.is_external ? `${report.ext_vehicle_brand} ${report.ext_vehicle_model || ''}` : `${report.brand} ${report.model}`}
          </span>
          <span className="kanban-card-reg">{report.is_external ? report.ext_vehicle_registration : report.registration_number}{report.is_external ? ' [ZEWN.]' : ''}</span>
        </div>

        <span className="kanban-card-category">{report.category}</span>

        <p className="kanban-card-desc">
          {report.description && report.description.length > 100
            ? report.description.substring(0, 100) + "…"
            : report.description}
        </p>

        <div className="kanban-card-meta">
          <span><i className="pi pi-user" />{report.reportedByName || "—"}</span>
          <span><i className="pi pi-calendar" />{formatDate(report.created_at)}</span>
        </div>

        {report.status === "RESOLVED" && report.resolution_notes && (
          <div className="kanban-card-resolution">
            <i className="pi pi-check-circle" />
            <span>{report.resolution_notes.length > 60 ? report.resolution_notes.substring(0, 60) + "…" : report.resolution_notes}</span>
          </div>
        )}

        {report.reopen_reason && (
          <div className="kanban-card-reopen">
            <i className="pi pi-undo" />
            <span>{report.reopen_reason.length > 60 ? report.reopen_reason.substring(0, 60) + "…" : report.reopen_reason}</span>
          </div>
        )}
      </div>

      <div className="kanban-card-actions">
        <Button icon="pi pi-images" rounded text size="small" severity="secondary" onClick={() => showPhotos(report)} tooltip="Zdjęcia" tooltipOptions={{ position: "top" }} />
        {report.status === "OPEN" && (
          <Button icon="pi pi-play" rounded text size="small" severity="info" onClick={() => markInProgress(report)} tooltip="Rozpocznij naprawę" tooltipOptions={{ position: "top" }} />
        )}
        {(report.status === "OPEN" || report.status === "IN_PROGRESS") && (
          <Button icon="pi pi-check" rounded text size="small" severity="success" onClick={() => openResolve(report)} tooltip="Naprawione" tooltipOptions={{ position: "top" }} />
        )}
        {report.status === "RESOLVED" && (
          <Button icon="pi pi-undo" rounded text size="small" severity="warning" onClick={() => openReopen(report)} tooltip="Cofnij do: W trakcie" tooltipOptions={{ position: "top" }} />
        )}
        {isAdmin && (
          <Button icon="pi pi-trash" rounded text size="small" severity="danger" onClick={() => deleteReport(report.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
        )}
      </div>
    </div>
  );
};

// --- Drag Overlay (ghost card while dragging) ---
const KanbanCardOverlay = ({ report, formatDate }) => (
  <div className="kanban-card kanban-card--overlay">
    <div className="kanban-card-grip">
      <i className="pi pi-ellipsis-v" /><i className="pi pi-ellipsis-v" />
    </div>
    <div className="kanban-card-content">
      <div className="kanban-card-top">
        <span className="kanban-card-vehicle">
          <i className="pi pi-car" /> {report.is_external ? `${report.ext_vehicle_brand} ${report.ext_vehicle_model || ''}` : `${report.brand} ${report.model}`}
        </span>
        <span className="kanban-card-reg">{report.is_external ? report.ext_vehicle_registration : report.registration_number}{report.is_external ? ' [ZEWN.]' : ''}</span>
      </div>
      <span className="kanban-card-category">{report.category}</span>
      <p className="kanban-card-desc">
        {report.description && report.description.length > 100
          ? report.description.substring(0, 100) + "…"
          : report.description}
      </p>
      <div className="kanban-card-meta">
        <span><i className="pi pi-user" />{report.reportedByName || "—"}</span>
        <span><i className="pi pi-calendar" />{formatDate(report.created_at)}</span>
      </div>
    </div>
  </div>
);

export default WorkshopManagerPanel;
